import { useState } from 'react'
import { CheckCircle2, ChevronDown, ChevronRight, Download, File, MessageSquare, XCircle } from 'lucide-react'
import type { AgentFile, Message } from '@/types'
import type { PlanningTask } from '@/hooks/useAgent'
import { SmoothStreamingText } from './SmoothStreamingText'
import { SineThinkingLoader } from '@/components/ui/SineThinkingLoader'

interface AgentNarrativeMessageProps {
  message: Message
  planningTasks?: PlanningTask[]
  onOpenFile?: (file: AgentFile, allFiles?: AgentFile[]) => void
  onSuggestion?: (text: string) => void
}

function cleanMojibake(value: string): string {
  return value
    .replace(/SÃ¸/g, 'Sø')
    .replace(/sÃ¸/g, 'sø')
    .replace(/Ã¸/g, 'ø')
    .replace(/Ã¥/g, 'å')
    .replace(/Ã¦/g, 'æ')
    .replace(/Ã˜/g, 'Ø')
    .replace(/Ã…/g, 'Å')
    .replace(/Ã†/g, 'Æ')
    .replace(/Â·/g, '·')
    .replace(/â˜†/g, '☆')
}

function cleanAgentText(value: string): string {
  if (!value) return ''
  return cleanMojibake(value)
    .split('\n')
    .filter(line => {
      const text = line.trim()
      if (!text) return true
      if (/^(web_search|browser|terminal|deploy_preview|run_code)\s*:/i.test(text)) return false
      if (/^[-*]?\s*(Step\s+\d+|Success\b|Partial\b|Failed\b|Unknown\b|Error\b|Clicked\b|Ready to\b|Looking at\b|The URL\b|I need to\b|I entered\b|Found the\b)/i.test(text)) return false
      if (/^(Søkefeil|E2B terminal-feil|Command exited with code|Deploy\/preview feilet)/i.test(text)) return false
      if (/^(deploy_preview|terminal|web_search|browser)\s+feilet/i.test(text)) return false
      if (/https?:\/\/(html|lite)\.duckduckgo\.com/i.test(text)) return false
      if (/DuckDuckGo.*(RateLimit|202)|context deadline exceeded|_asyncio\.Task|CAPTCHA|Cloudflare|privacy dialog|cookie|search input field|button index|browser-use|about:blank/i.test(text)) return false
      if (/^'_asyncio\.Task' object has no attribute/i.test(text)) return false
      return true
    })
    .join('\n')
    .replace(/Det oppstod noen begrensninger underveis:/gi, 'Forbehold:')
    .replace(/Jeg har fortsatt prøvd å levere det jeg kunne, og vi kan fortsette fra dette punktet\./gi, 'Jeg har levert det som er trygt basert på tilgjengelige kilder.')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function toolLabel(tool?: string): string {
  const labels: Record<string, string> = {
    web_search: 'Søker kilder',
    browser: 'Leser nettside',
    terminal: 'Kjører terminal',
    write_file: 'Skriver fil',
    read_file: 'Leser fil',
    list_files: 'Sjekker filer',
    deploy_preview: 'Publiserer preview',
    run_code: 'Kjører kode',
  }
  return labels[tool ?? ''] ?? (tool ? tool.replaceAll('_', ' ') : 'Utfører')
}

function chipLabel(chip: PlanningTask['chips'][number]): string {
  const label = cleanAgentText(chip.label ?? '').replace(/^(Kjører|Starter|Bruker)\s+/i, '').trim()
  if (label && !/^(web_search|browser|terminal|run_code|write_file|read_file|list_files)$/i.test(label)) {
    return label.length > 56 ? `${label.slice(0, 53).trim()}...` : label
  }
  return toolLabel(chip.tool)
}

function visibleChips(chips: PlanningTask['chips']): PlanningTask['chips'] {
  const seen = new Set<string>()
  const result: PlanningTask['chips'] = []
  for (const chip of chips) {
    const label = chipLabel(chip)
    const key = `${chip.tool}:${label}:${chip.success === false ? 'error' : chip.success ? 'done' : 'run'}`
    if (seen.has(key)) continue
    seen.add(key)
    result.push(chip)
  }
  return result.slice(-4)
}

function stepUpdates(step: PlanningTask): string[] {
  const values = [...(step.updates ?? [])]
  if (step.prose) values.push(step.prose)
  const seen = new Set<string>()
  return values
    .map(cleanAgentText)
    .filter(Boolean)
    .filter(text => !/^(Venter til dette steget|Jeg jobber med dette)/i.test(text))
    .filter(text => {
      const key = text.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .slice(-8)
}

function statusLabel(status?: Message['agentStatus']): string {
  if (status === 'completed') return 'Oppgave fullført'
  if (status === 'completed_with_warnings') return 'Oppgave levert med forbehold'
  if (status === 'failed' || status === 'fatal_failed') return 'Oppgave stoppet'
  if (status === 'interrupted' || status === 'resume_available') return 'Kjøringen ble avbrutt'
  if (status === 'stopped') return 'Stoppet'
  return 'Tenker'
}

function activeVoiceLabel(value: string): string {
  const label = cleanAgentText(value).trim()
  if (/^Forst[åa]\s+hva\s+som\s+menes\b.*\bverdi\b/i.test(label)) {
    return 'Estimerer boligverdi'
  }
  const replacements: Array<[RegExp, string]> = [
    [/^Søk\s+etter\b/i, 'Søker etter'],
    [/^Søk\b/i, 'Søker'],
    [/^Finn\s+ut\b/i, 'Finner ut'],
    [/^Finn\b/i, 'Finner'],
    [/^Samle\b/i, 'Samler'],
    [/^Not[eé]r\b/i, 'Noterer'],
    [/^Beregn\b/i, 'Beregner'],
    [/^Skriv\b/i, 'Skriver'],
    [/^Lag\b/i, 'Lager'],
    [/^Les\b/i, 'Leser'],
    [/^Analyser\b/i, 'Analyserer'],
    [/^Sammenlign\b/i, 'Sammenligner'],
    [/^Åpne\b/i, 'Åpner'],
    [/^Hent\b/i, 'Henter'],
    [/^Kontroller\b/i, 'Kontrollerer'],
    [/^Gjennomgå\b/i, 'Går gjennom'],
    [/^Opprett\b/i, 'Oppretter'],
    [/^Pakk\b/i, 'Pakker'],
    [/^Verifiser\b/i, 'Verifiserer'],
    [/^Dokumenter\b/i, 'Dokumenterer'],
  ]
  for (const [pattern, replacement] of replacements) {
    if (pattern.test(label)) return label.replace(pattern, replacement)
  }
  return label
}

function visibleSteps(steps: PlanningTask[], status?: Message['agentStatus']): PlanningTask[] {
  if (steps.length === 0) return []
  const terminal = status === 'completed' || status === 'completed_with_warnings' || status === 'failed' || status === 'fatal_failed' || status === 'stopped' || status === 'interrupted' || status === 'resume_available'
  if (terminal) return steps
  const meaningful = steps.filter((step, index) =>
    step.status !== 'pending' ||
    index === 0 ||
    (step.chips?.length ?? 0) > 0 ||
    (step.updates?.length ?? 0) > 0 ||
    Boolean(step.prose)
  )
  return meaningful.length > 0 ? meaningful : steps.slice(0, 1)
}

function confirmationText(steps: PlanningTask[], isDone: boolean, isFailed: boolean): string {
  if (isDone) return 'Jeg har samlet arbeidet og leverer resultatet på en ryddig måte her.'
  if (isFailed) return 'Jeg kom ikke helt i mål, men jeg viser nøyaktig hvor det stoppet og hva vi kan gjøre videre.'

  const firstLabels = steps.slice(0, 3).map(step => step.label.toLowerCase()).join(' ')
  if (/burger|restaurant|spisested|mat/.test(firstLabels)) {
    return 'Den er god. Jeg finner relevante burgersteder, sammenligner kilder og lager en kort rapport du kan bruke.'
  }
  if (/bolig|leilighet|verdi|pris|eiendom/.test(firstLabels)) {
    return 'Den er god. Jeg undersøker boligdata, sammenligner relevante kilder og lager et estimat med tydelige forbehold.'
  }
  if (/rapport|research|undersøk/.test(firstLabels)) {
    return 'Den er god. Jeg gjør researchen steg for steg og samler funnene i en ryddig rapport.'
  }
  if (/kode|implementer|fiks|debug/.test(firstLabels)) {
    return 'Den er god. Jeg går gjennom problemet, finner rotårsaken og jobber meg mot en trygg endring.'
  }
  return 'Den er god. Jeg går gjennom oppgaven steg for steg og holder deg oppdatert underveis.'
}

function introTitle(steps: PlanningTask[], isDone: boolean, isFailed: boolean): string {
  if (isDone) return 'Jeg har fullført arbeidet.'
  if (isFailed) return 'Jeg stoppet før alt var ferdig.'
  const firstLabel = activeVoiceLabel(steps[0]?.label ?? '').trim()
  if (!firstLabel) return 'Jeg starter med å forstå oppgaven.'
  return `${firstLabel}.`
}

function downloadFile(file: AgentFile) {
  if (file.content) {
    const blob = new Blob([file.content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    a.click()
    URL.revokeObjectURL(url)
    return
  }
  if (file.downloadUrl) {
    const a = document.createElement('a')
    a.href = file.downloadUrl
    a.download = file.name
    a.click()
  }
}

export function AgentNarrativeMessage({ message, planningTasks = [], onOpenFile, onSuggestion }: AgentNarrativeMessageProps) {
  const [replayOpen, setReplayOpen] = useState(false)
  const status = message.agentStatus ?? 'running'
  const isDone = status === 'completed' || status === 'completed_with_warnings'
  const isFailed = status === 'failed' || status === 'fatal_failed' || status === 'stopped' || status === 'interrupted' || status === 'resume_available'
  const files = message.agentFiles ?? []
  const suggestions = (message.agentSuggestions ?? []).filter(Boolean).slice(0, 4)
  const bodyText = cleanAgentText(message.content)
  const steps = visibleSteps(planningTasks, status)
  const hasSteps = steps.length > 0
  const replayEvents = (message.agentEvents ?? [])
    .filter(event => event.label?.trim())
    .slice(-12)

  return (
    <div className="agent-narrative-message">
      <div className="agent-narrative-brand" aria-label="Sine">
        <img className="agent-narrative-brand-logo agent-narrative-brand-logo--dark" src="/Sine-hvit.svg" alt="" aria-hidden="true" />
        <img className="agent-narrative-brand-logo agent-narrative-brand-logo--light" src="/Sine-sort.svg" alt="" aria-hidden="true" />
      </div>

      {!bodyText && !hasSteps && (
        <div className="agent-narrative-thinking">
          <SineThinkingLoader size="sm" label="Tenker" />
        </div>
      )}

      {hasSteps && (
        <section className="agent-narrative-plan-v2">
          <div className="agent-narrative-intro">
            <strong>{introTitle(steps, isDone, isFailed)}</strong>
            <p className="agent-narrative-confirmation">{confirmationText(steps, isDone, isFailed)}</p>
          </div>

          <div className="agent-narrative-step-list">
            {steps.map((step, index) => {
              const done = step.status === 'done'
              const active = step.status === 'active'
              const error = step.status === 'error'
              const last = index === steps.length - 1
              const updates = stepUpdates(step)
              const chips = visibleChips(step.chips ?? [])
              return (
                <div
                  key={`${step.id ?? index}-${step.label}`}
                  className={[
                    'agent-narrative-step-v2',
                    done ? 'is-done' : '',
                    active ? 'is-active' : '',
                    error ? 'is-error' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <div className="agent-narrative-step-rail">
                    <span className="agent-narrative-step-dot">
                      {done ? <CheckCircle2 size={13} /> : error ? <XCircle size={13} /> : index + 1}
                    </span>
                    {!last && <span className="agent-narrative-step-line" />}
                  </div>
                  <div className="agent-narrative-step-body">
                    <div className="agent-narrative-step-title">
                      <strong>{activeVoiceLabel(step.label)}</strong>
                      {step.tool_hint && <span>{toolLabel(step.tool_hint)}</span>}
                    </div>
                    {updates.length > 0 ? (
                      <div className="agent-narrative-update-list">
                        {updates.map((update, updateIndex) => (
                          <p key={`${step.id ?? index}-update-${updateIndex}`}>{update}</p>
                        ))}
                      </div>
                    ) : (
                      <p>{active ? 'Jeg jobber med dette nå.' : 'Venter til dette steget er klart.'}</p>
                    )}
                    {chips.length > 0 && (
                      <div className="agent-narrative-chip-row">
                        {chips.map(chip => (
                          <span key={chip.id} className={`agent-narrative-chip${chip.success === false ? ' is-error' : chip.success ? ' is-done' : ' is-active'}`}>
                            {chipLabel(chip)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {bodyText && (
        <div className="agent-narrative-answer">
          <SmoothStreamingText
            content={bodyText}
            animateOnMount
            animationKey={`${message.id}-answer`}
          />
        </div>
      )}

      {files.length > 0 && (
        <section className="agent-narrative-delivery">
          <div className="agent-narrative-delivery-head">
            <strong>Leveranse</strong>
            <span>{files.length} fil{files.length === 1 ? '' : 'er'} klar{files.length === 1 ? '' : 'e'}.</span>
          </div>
          <div className="agent-narrative-file-grid">
            {files.slice(0, 3).map(file => (
              <button key={file.path} type="button" className="agent-narrative-file" onClick={() => onOpenFile?.(file, files)}>
                <File size={18} />
                <span>
                  <strong>{file.name}</strong>
                  <small>{file.type}{file.size ? ` · ${file.size}` : ''}</small>
                </span>
                <Download size={15} onClick={event => { event.stopPropagation(); downloadFile(file) }} />
              </button>
            ))}
            {files.length > 3 && (
              <button type="button" className="agent-narrative-file is-view-all" onClick={() => onOpenFile?.(files[0], files)}>
                <File size={18} />
                <span><strong>Se alle filer i denne oppgaven</strong></span>
                <ChevronRight size={15} />
              </button>
            )}
          </div>
        </section>
      )}

      {(isDone || isFailed) && (
        <div className={`agent-narrative-status is-${isFailed ? 'failed' : 'done'}`}>
          {isFailed ? <XCircle size={14} /> : <CheckCircle2 size={14} />}
          <span>{statusLabel(status)}</span>
          {!isFailed && (
            <span className="agent-narrative-rating">
              Hvordan var resultatet?
              {[1, 2, 3, 4, 5].map(item => <button key={item} type="button">☆</button>)}
            </span>
          )}
        </div>
      )}

      {replayEvents.length > 0 && (
        <section className="agent-narrative-replay">
          <button type="button" onClick={() => setReplayOpen(value => !value)}>
            <span>Kjøringsreplay</span>
            <small>{replayEvents.length} hendelser</small>
            {replayOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          {replayOpen && (
            <div className="agent-narrative-replay-list">
              {replayEvents.map(event => (
                <div key={event.id} className={`agent-narrative-replay-row is-${event.success === false ? 'error' : event.success ? 'done' : 'info'}`}>
                  <span />
                  <p>{cleanAgentText(event.label)}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {suggestions.length > 0 && (
        <section className="agent-narrative-followups">
          <p>Foreslåtte oppfølginger</p>
          {suggestions.map((suggestion, index) => (
            <button key={`${suggestion}-${index}`} type="button" onClick={() => onSuggestion?.(suggestion)}>
              <MessageSquare size={14} />
              <span>{suggestion.replace(/\*\*/g, '').replace(/^#+\s*/, '').trim()}</span>
              <ChevronRight size={14} />
            </button>
          ))}
        </section>
      )}
    </div>
  )
}
